import { originState, useOriginSource, withFrom } from '@/lib/origin';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AtSign, BarChart3, Bell, Bug, CalendarClock, CalendarDays, ShieldCheck, CheckCheck, CheckSquare, MessageSquare, Phone, Plane } from 'lucide-react';
import type { NotificationItem } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Spinner } from '@/components/ui';
import { RelativeTime } from '@/components/time/RelativeTime';

export function NotificationIcon({ kind }: { kind: string }) { return iconFor(kind); }

function iconFor(kind: string) {
  if (kind.includes('mention')) return <AtSign className="h-4 w-4" />;
  if (kind.startsWith('feedback')) return <Bug className="h-4 w-4" />;
  if (kind.startsWith('task')) return <CheckSquare className="h-4 w-4" />;
  if (kind.startsWith('call')) return <Phone className="h-4 w-4" />;
  if (kind.startsWith('poll')) return <BarChart3 className="h-4 w-4" />;
  if (kind.startsWith('calendar')) return <CalendarDays className="h-4 w-4" />;
  if (kind.startsWith('ack.')) return <ShieldCheck className="h-4 w-4" />;
  if (kind.includes('leave')) return <Plane className="h-4 w-4" />;
  if (kind.startsWith('workforce')) return <CalendarClock className="h-4 w-4" />;
  if (kind.startsWith('message') || kind.includes('thread')) return <MessageSquare className="h-4 w-4" />;
  return <Bell className="h-4 w-4" />;
}

/** Navigate to an in-app link, opening the task panel for `?task=` links. */
export function useOpenLink() {
  const nav = useNavigate();
  const openTask = useUi(s => s.openTask);
  const origin = useOriginSource();
  return (link: string | null) => {
    if (!link) return;
    nav(withFrom(link, origin), { state: originState(origin) });
    const task = new URL(link, window.location.origin).searchParams.get('task');
    if (task) openTask(task);
  };
}

export function NotificationPanel({ items: all, loading, onClose, filter = 'all', onFilter }: { items: NotificationItem[]; loading: boolean; onClose: () => void; filter?: 'all' | 'unread'; onFilter?: (f: 'all' | 'unread') => void }) {
  const items = filter === 'unread' ? all.filter(n => !n.read_at) : all;
  const ws = useWorkspaceId();
  const ref = useRef<HTMLDivElement>(null);
  const openLink = useOpenLink();

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const t = setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', h); };
  }, [onClose]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: qk.notifications(ws ?? '') });

  const click = async (n: NotificationItem) => {
    if (!n.read_at) await api.markNotificationRead(n.id).catch(() => undefined);
    refresh();
    onClose();
    openLink(n.link);
  };

  return (
    <div ref={ref} className="absolute right-0 top-11 z-40 w-96 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="font-semibold">Notifications</div>
          {onFilter && (
            <div className="flex gap-1 text-xs" role="tablist" aria-label="Show">
              {(['all', 'unread'] as const).map(f => (
                <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => onFilter(f)} data-testid={`notifications-filter-${f}`}
                  className={clsx('rounded-full px-2 py-0.5 font-medium', filter === f ? 'bg-accent text-on-accent' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800')}>{f === 'all' ? 'All' : 'Unread'}</button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={async () => { if (ws) await api.markAllNotificationsRead(ws); refresh(); }}><CheckCheck className="h-4 w-4" />Mark all read</Button>
      </div>
      <div className="scroll-thin max-h-[28rem] overflow-y-auto">
        {loading && <div className="flex justify-center p-6"><Spinner /></div>}
        {!loading && items.length === 0 && <div className="p-8 text-center text-sm text-slate-500">{filter === 'unread' && all.length ? 'No unread notifications.' : "You're all caught up."}</div>}
        {items.map(n => (
          <button key={n.id} onClick={() => void click(n)} className={clsx('flex w-full gap-3 border-b border-l-[3px] border-b-slate-50 py-3 pl-[13px] pr-4 text-left hover:bg-slate-50 dark:border-b-slate-800 dark:hover:bg-slate-800', n.read_at ? 'border-l-transparent' : 'border-l-brand-yellow')}>
            <span className={clsx('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', n.read_at ? 'bg-slate-100 text-slate-500 dark:bg-slate-800' : 'bg-accent text-on-accent')}>{iconFor(n.kind)}</span>
            <span className="min-w-0 flex-1">
              <span className={clsx('block text-sm', n.read_at ? 'font-medium' : 'font-semibold')}>{n.title}</span>
              {n.body && <span className="line-clamp-2 block text-sm text-slate-500">{n.body}</span>}
              <span className="mt-0.5 block text-xs text-slate-500"><RelativeTime iso={n.created_at} /></span>
            </span>
            {!n.read_at && <span className="sr-only">Unread</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
